import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldHalf, Menu, X, ChevronDown, ClipboardList, Wifi } from "lucide-react";
import { company } from "../../mock";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../ui/dropdown-menu";

const links = [
  { label: "Internet & TV", href: "/#connectivity" },
  { label: "Solutions", href: "/#solutions" },
  { label: "Suppliers", href: "/#suppliers" },
  { label: "Why Us", href: "/#why-us" },
  { label: "Resources", href: "/#resources" },
  { label: "FAQ", href: "/#faq" },
];

export const CONTACT_OPTIONS = [
  { to: "/intake", testid: "contact-option-discovery", icon: ClipboardList, title: "Technology Discovery Form", desc: "Cybersecurity, cloud, AI, networking, UCaaS & more" },
  { to: "/connectivity-inquiry", testid: "contact-option-connectivity", icon: Wifi, title: "Internet, TV & Satellite Inquiry", desc: "Quick two-minute form for business connectivity" },
];

const ContactMenu = () => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <button data-testid="header-contact-btn"
        className="inline-flex items-center gap-1.5 text-sm font-semibold bg-cyan-400 text-[#05071F] px-5 py-2 rounded-full hover:bg-cyan-300 transition-colors data-[state=open]:bg-cyan-300">
        Contact Us <ChevronDown className="w-4 h-4" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" sideOffset={10} data-testid="header-contact-menu"
      className="w-80 p-2 bg-[#0B1035] border-white/15 text-white shadow-2xl shadow-black/50">
      {CONTACT_OPTIONS.map(({ to, testid, icon: Icon, title, desc }) => (
        <DropdownMenuItem key={to} asChild className="p-0 focus:bg-white/5 focus:text-white">
          <Link to={to} data-testid={testid} className="flex items-start gap-3 px-3 py-3 rounded-md cursor-pointer">
            <Icon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
            <span>
              <span className="block text-sm font-semibold">{title}</span>
              <span className="block text-xs text-slate-400 mt-0.5">{desc}</span>
            </span>
          </Link>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header data-testid="site-header" className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#05071F]/70 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex justify-between items-center">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 text-white">
          <ShieldHalf className="w-6 h-6 text-cyan-400" />
          <span className="font-heading font-semibold tracking-tight text-lg">{company.name}</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a key={l.label} href={l.href} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm text-slate-400 hover:text-white transition-colors">
              {l.label}
            </a>
          ))}
          <ContactMenu />
        </nav>
        <button data-testid="mobile-menu-btn" className="md:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <nav data-testid="mobile-menu" className="md:hidden bg-[#05071F] border-t border-white/10 px-6 py-4 flex flex-col gap-4">
          {links.map((l) => (
            <a key={l.label} href={l.href} data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setOpen(false)} className="text-slate-300 hover:text-white transition-colors">{l.label}</a>
          ))}
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500 pt-2 border-t border-white/10">Contact Us</p>
          {CONTACT_OPTIONS.map(({ to, testid, icon: Icon, title }) => (
            <Link key={to} to={to} data-testid={`mobile-${testid}`} onClick={() => setOpen(false)} className="inline-flex items-center gap-2 text-cyan-400 font-semibold">
              <Icon className="w-4 h-4" /> {title}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
};
